import {
  BasketballAction,
  BasketballCourtView,
  BasketballMarker,
  HockeyRinkView,
  SoccerPitchView,
  SportPlay,
} from "../../../../types";
import { Sport } from "../../sport-slice";
import { BasketballDrawingArea, HockeyDrawingArea, PlayPayload, PlayPayloadBase, SoccerDrawingArea } from "../types";
import { convertBBFrameStencilToDBStencil } from "./convert-bb-frame-stencil-to-db-stencil";

const hockeyRinkViewToDrawingAreaMap = new Map<HockeyRinkView, HockeyDrawingArea>([
  [HockeyRinkView.FULL, HockeyDrawingArea.FULL],
  [HockeyRinkView.LEFT_RINK, HockeyDrawingArea.LEFT_RINK],
  [HockeyRinkView.RIGHT_RINK, HockeyDrawingArea.RIGHT_RINK],
  [HockeyRinkView.GOAL, HockeyDrawingArea.GOAL],
  [HockeyRinkView.LEFT_ONE_THIRD, HockeyDrawingArea.LEFT_ONE_THIRD],
  [HockeyRinkView.RIGHT_ONE_THIRD, HockeyDrawingArea.RIGHT_ONE_THIRD],
  [HockeyRinkView.LEFT_TWO_THIRD, HockeyDrawingArea.LEFT_TWO_THIRD],
  [HockeyRinkView.RIGHT_TWO_THIRD, HockeyDrawingArea.RIGHT_TWO_THIRD],
]);

const BasketballCourtViewToDrawingAreaMap = new Map<BasketballCourtView, BasketballDrawingArea>([
  [BasketballCourtView.FRONTCOURT, BasketballDrawingArea.FRONTCOURT],
  [BasketballCourtView.BACKCOURT, BasketballDrawingArea.BACKCOURT],
  [BasketballCourtView.FULLCOURT, BasketballDrawingArea.FULLCOURT],
]);

const soccerPitchViewToDrawingAreaMap = new Map<SoccerPitchView, SoccerDrawingArea>([
  [SoccerPitchView.FULL, SoccerDrawingArea.FULL],
  [SoccerPitchView.LEFT_HALF, SoccerDrawingArea.LEFT_HALF],
  [SoccerPitchView.RIGHT_HALF, SoccerDrawingArea.RIGHT_HALF],
  [SoccerPitchView.LEFT_QUARTER, SoccerDrawingArea.LEFT_QUARTER],
  [SoccerPitchView.RIGHT_QUARTER, SoccerDrawingArea.RIGHT_QUARTER],
]);

export const convertSportPlayToPlayPayload = (basePlay: SportPlay): PlayPayload => {
  const {
    id,
    drawId,
    sport,
    name,
    team,
    series,
    season,
    tags,
    notes,
    frames,
    playSurfaceId,
    createdTimestamp,
    updatedTimestamp,
    recipients,
    orientation,
  } = basePlay;

  const basePayload: PlayPayloadBase = {
    id,
    drawId,
    name,
    season,
    teamId: team?.team_id || null,
    seriesId: series?.series_id || null,
    tags,
    notes,
    courtType: playSurfaceId,
    createdTimestamp,
    updatedTimestamp,
    recipientsDTO: recipients,
  };

  switch (sport) {
    case Sport.BASKETBALL: {
      const outOfBounds = {
        outOfBoundsEnd: basePlay.properties.outOfBoundsEnd,
        outOfBoundsLeft: basePlay.properties.outOfBoundsLeft,
        outOfBoundsRight: basePlay.properties.outOfBoundsRight,
      };

      return {
        ...basePayload,
        sport: Sport.BASKETBALL,
        drawingArea:
          BasketballCourtViewToDrawingAreaMap.get(basePlay.playSurfaceView) || BasketballDrawingArea.FRONTCOURT,
        framesDTO: frames.map((frame) => {
          return {
            ...frame,
            d3Stencils: convertBBFrameStencilToDBStencil(
              frame.frameStencils as (BasketballAction | BasketballMarker)[]
            ),
            // keep existing bezierStencils so that old frames on mobile are not blank when saved with ?done=false
            bezierStencils: frame.bezierStencils ?? null,
          };
        }),
        properties: {
          outOfBounds: JSON.stringify(outOfBounds),
          playOptions: JSON.stringify(basePlay.courtOptions),
          orientation,
        },
      };
    }

    case Sport.HOCKEY: {
      const playOptions = JSON.stringify(basePlay.courtOptions);
      const drawingArea = hockeyRinkViewToDrawingAreaMap.get(basePlay.playSurfaceView) || HockeyDrawingArea.FULL;

      return {
        ...basePayload,
        sport: Sport.HOCKEY,
        drawingArea,
        framesDTO: frames.map((frame) => ({
          ...frame,
          d3Stencils: JSON.stringify(frame.frameStencils),
          // keep existing bezierStencils so that old frames on mobile are not blank when saved with ?done=false
          bezierStencils: frame.bezierStencils ?? null,
        })),
        properties: { ...basePlay.properties, ...(playOptions ? { playOptions } : {}), orientation },
      };
    }

    case Sport.SOCCER: {
      const playOptions = JSON.stringify(basePlay.courtOptions);
      const drawingArea = soccerPitchViewToDrawingAreaMap.get(basePlay.playSurfaceView) || SoccerDrawingArea.FULL;

      return {
        ...basePayload,
        sport: Sport.SOCCER,
        drawingArea,
        framesDTO: frames.map((frame) => ({
          ...frame,
          d3Stencils: JSON.stringify(frame.frameStencils),
          // keep existing bezierStencils so that old frames on mobile are not blank when saved with ?done=false
          bezierStencils: frame.bezierStencils ?? null,
        })),
        properties: {
          ...basePlay.properties,
          ...(playOptions ? { playOptions } : {}),
          orientation,
          outOfBounds: basePlay.outOfBounds,
        },
      };
    }
  }
};
